import React, {useCallback, useMemo, useRef, useState} from "react";
import {useLoaderData} from "react-router";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import PeriodInput from "../../../components/input/period-input.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import ShowHideColumnsGrid from "../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaFileExcel, FaPaperclip, FaQuestion, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import projectUtils from "../../../utils/project-utils.js";
import DispatchService from "../../../services/dispatch/dispatch-service.js";
import DispatchListOptionsService from "../../../services/dispatch/dispatch-list-options-service.js";
import AnimalsService from "../../../services/web/animals-service.js";
import {
    exportGridData,
    sanitizeFilterValue,
    selectedValue,
    selectedValues,
    validateDateInterval,
} from "../utils/dispatch-list-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const occurrenceStatusOptions = [
    {value: "NUMBER_GENERATED", label: "Numeração gerada"},
    {value: "DRAFT", label: "Em preenchimento"},
    {value: "APP_DELETED", label: "Excluído (app)"},
    {value: "SYSTEM_DELETED", label: "Excluído (sistema)"},
    {value: "FILE_SENT", label: "Documento enviado"},
    {value: "PENDING", label: "Pendente (aprovação)"},
    {value: "REVIEWED", label: "Revisado"},
    {value: "REJECTED", label: "Reprovado"},
    {value: "APPROVED", label: "Aprovado"},
    {value: "INVALIDATED", label: "Invalidado"},
];

const initialFilters = {
    start: "",
    end: "",
    identifier: "",
    identifierBo: "",
    hasBo: null,
    occurrenceStatus: null,
    contingent: null,
    reason: [],
    providence: [],
    animal: [],
    address: "",
    garrison: "",
    contact: "",
    containedText: "",
};

const buildFilter = (values) => [
    {field: "date", type: "keywords", value: {dtStart: values.start || "", dtEnd: values.end || ""}},
    {field: "has_bo", type: "like", value: selectedValue(values.hasBo)},
    {field: "identifier", type: "like", value: sanitizeFilterValue(values.identifier)},
    {field: "identifier_bo", type: "like", value: sanitizeFilterValue(values.identifierBo)},
    {field: "keyword", type: "like", value: sanitizeFilterValue(values.containedText)},
    {field: "occurrence_status", type: "like", value: selectedValue(values.occurrenceStatus)},
    {field: "schedule", type: "like", value: false},
    {field: "address", type: "like", value: sanitizeFilterValue(values.address)},
    {field: "contingent", type: "=", value: selectedValue(values.contingent)},
    {field: "reason", type: "in", value: selectedValues(values.reason)},
    {field: "providence", type: "in", value: selectedValues(values.providence)},
    {field: "animal", type: "in", value: selectedValues(values.animal)},
    {field: "garrison", type: "like", value: sanitizeFilterValue(values.garrison)},
    {field: "contact", type: "like", value: sanitizeFilterValue(values.contact).replace(/[.-]/g, "")},
];

const durationComparator = (valueA, valueB, nodeA, nodeB, isDescending) => {
    if (valueA === valueB) return 0;
    if (!valueA) return isDescending ? -1 : 1;
    if (!valueB) return isDescending ? 1 : -1;

    const first = Number(nodeA?.data?.dateDiffTs ?? -1);
    const second = Number(nodeB?.data?.dateDiffTs ?? -1);

    return first - second;
};

const exportRow = (row, boName) => ({
    "Id": row.id,
    "N° Talão": row.numberCheckbook,
    [`N° ${boName}`]: row.occurrenceNumber,
    "Data/Hora inicial": row.start,
    "Data/Hora final": row.end,
    "Anexos": row.attachment ? "Sim" : "",
    "Guarnição": row.garrison,
    "Natureza": row.nature,
    "Endereço do fato": row.place,
    "Apoiando a guarnição": row.supportForGarrison,
    "Status": row.status,
    "Duração": row.dateDiff,
});

const HIDDEN_COLUMNS_STORAGE = "dispatch-hidden-columns";
const DEFAULT_HIDDEN_COLUMNS = ["id", "supportForGarrison"];

const buildColumnDefs = (boName) => {
    const baseColumns = [
        {field: "id", headerName: "ID", sort: "desc", width: 90},
        {field: "numberCheckbook", headerName: "N° Talão", width: 130, comparator: aggridUtils.agGridNumberYearComparator},
        {field: "occurrenceNumber", headerName: `N° ${boName}`, width: 130, comparator: aggridUtils.agGridNumberYearComparator},
        {field: "start", headerName: "Data/Hora inicial", width: 160, comparator: aggridUtils.agGridCompareDateTime},
        {field: "end", headerName: "Data/Hora final", width: 160, comparator: aggridUtils.agGridCompareDateTime},
        {
            field: "attachment",
            headerName: "Anexos",
            width: 80,
            sortable: false,
            cellRenderer: ({value}) => value ? <span className={"w-full flex justify-center items-center h-full"}><FaPaperclip /></span> : "",
        },
        {field: "garrison", headerName: "Guarnição", minWidth: 260, flex: 1},
        {field: "nature", headerName: "Natureza", minWidth: 260, flex: 1},
        {field: "place", headerName: "Endereço do fato", minWidth: 380, flex: 1},
        {field: "supportForGarrison", headerName: "Apoiando a guarnição", minWidth: 210},
        {field: "status", headerName: "Status", minWidth: 130},
        {field: "dateDiff", headerName: "Duração", minWidth: 130, comparator: durationComparator},
    ];

    const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? DEFAULT_HIDDEN_COLUMNS;
    return baseColumns.map(column => ({...column, hide: hiddenColumns.includes(column.field)}));
};

const DispatchList = () => {
    const loaderData = useLoaderData();
    const navigate = navigateHook();
    const gridRef = useRef(null);
    const boName = loaderData?.bo_name ?? "BO";
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const hasBoOptions = useMemo(() => [
        {value: "All", label: "Todos"},
        {value: "withBO", label: `Com ${boName}`},
        {value: "withoutBO", label: `Sem ${boName}`},
    ], [boName]);

    const {data = [], isLoading} = useQuery({
        queryFn: () => DispatchService.list({filter: [...filter]}),
        queryKey: ["dispatch-dispatch", {filter}],
        placeholderData: keepPreviousData,
    });

    const {data: contingentOptions = [], isLoading: isLoadingContingents} = useQuery({
        queryFn: () => DispatchListOptionsService.contingents(),
        queryKey: ["dispatch-list-contingents"],
    });

    const {data: natureOptions = [], isLoading: isLoadingNatures} = useQuery({
        queryFn: () => DispatchListOptionsService.dispatchNatures(),
        queryKey: ["dispatch-natures"],
    });

    const {data: providenceOptions = [], isLoading: isLoadingProvidences} = useQuery({
        queryFn: () => DispatchListOptionsService.dispatchProvidences(),
        queryKey: ["dispatch-providences"],
    });

    const {data: animalOptions = [], isLoading: isLoadingAnimals} = useQuery({
        queryFn: () => AnimalsService.all(),
        queryKey: ["dispatch-list-animals"],
    });

    const [columnDefs, setColumnDefs] = useState(() => buildColumnDefs(boName));

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => {
        const message = validateDateInterval(filterValues.start, filterValues.end);
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        setFilter(buildFilter(filterValues));
    }, [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, []);

    const onRowClicked = ({data: row}) => {
        if (!row?.id) return;
        navigate(`${API_DESPACHO_WEB}/dispatch/form/${row.id}`, true);
    };

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading || isLoadingContingents || isLoadingNatures || isLoadingProvidences || isLoadingAnimals}>
            <Template.Header>
                <Template.Title>Despachos</Template.Title>
                <Button target="success" onClick={() => exportGridData(gridRef.current?.api, (row) => exportRow(row, boName))} title="Exportar">
                    <FaFileExcel/> Exportar
                </Button>
            </Template.Header>
            <Template.Body className="flex flex-col h-full min-h-0 space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow className="items-start">
                            <PeriodInput
                                startDateId="start"
                                endDateId="end"
                                label="Início entre"
                                dateFormat="LL"
                                returnDateObj={false}
                                startDate={filterValues.start}
                                endDate={filterValues.end}
                                setFilter={setFilterValues}
                                cols={6}
                            />
                            <InputForm classNameWrapper="col-span-2" id="identifier" label="Talão" value={filterValues.identifier} onChange={handleInput}/>
                            <InputForm classNameWrapper="col-span-2" id="identifierBo" label={`${boName} Nº`} value={filterValues.identifierBo} onChange={handleInput}/>
                            <SelectComponent
                                classNameWrapper="col-span-2"
                                id="hasBo"
                                label={boName}
                                options={hasBoOptions}
                                value={filterValues.hasBo}
                                onChange={(option) => setFilterValues(prev => ({...prev, hasBo: option}))}
                            />

                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="occurrenceStatus"
                                label={`${boName} status`}
                                options={occurrenceStatusOptions}
                                value={filterValues.occurrenceStatus}
                                onChange={(option) => setFilterValues(prev => ({...prev, occurrenceStatus: option}))}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="contingent"
                                label="Contingente"
                                options={contingentOptions}
                                value={filterValues.contingent}
                                onChange={(option) => setFilterValues(prev => ({...prev, contingent: option}))}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="animal"
                                label="Animais"
                                isMulti
                                wrapValues
                                options={animalOptions}
                                value={filterValues.animal}
                                onChange={(options) => setFilterValues(prev => ({...prev, animal: options ?? []}))}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="reason"
                                label="Natureza"
                                isMulti
                                wrapValues
                                options={natureOptions}
                                value={filterValues.reason}
                                onChange={(options) => setFilterValues(prev => ({...prev, reason: options ?? []}))}
                            />

                            <SelectComponent
                                classNameWrapper="col-span-4"
                                id="providence"
                                label="Providência"
                                isMulti
                                wrapValues
                                options={providenceOptions}
                                value={filterValues.providence}
                                onChange={(options) => setFilterValues(prev => ({...prev, providence: options ?? []}))}
                            />
                            <InputForm classNameWrapper="col-span-4" id="address" label="Endereço" value={filterValues.address} onChange={handleInput}/>
                            <InputForm classNameWrapper="col-span-4" id="garrison" label="Guarnição" value={filterValues.garrison} onChange={handleInput}/>

                            <InputForm classNameWrapper="col-span-5" id="contact" label="Solicitante" value={filterValues.contact} onChange={handleInput}/>
                            <div className="col-span-7 flex gap-4">
                                <InputForm
                                    classNameWrapper="w-full"
                                    id="containedText"
                                    label="Texto contido"
                                    inputClass="pr-9"
                                    title="Filtros: Observação inicial e observação final"
                                    value={filterValues.containedText}
                                    onChange={handleInput}
                                    span={
                                        <span
                                            className="absolute right-2 top-1/2 z-20 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#E9ECEF] text-[#676a6c]"
                                            title="Filtros: Observação inicial e observação final"
                                            aria-label="Campos pesquisados pelo filtro Texto contido"
                                        >
                                            <FaQuestion size={10}/>
                                        </span>
                                    }
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                                    <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-285px)] aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default DispatchList;
